import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  ListGroup,
  Badge,
} from 'react-bootstrap';
import ElegantModal from '../../components/ElegantModal/ElegantModal';
import DateInput from '../../components/DateInput/DateInput';
import SistemaToast from '../../components/SistemaToast/SistemaToast';
import { urlApiProd } from '../utiles';
import { parsePackingListExcel } from './parsePackingListExcel';
import { isRwNrPackingListRow } from './packingListRwNr';
import { sortPackingListDetailByGradeWithinContainer } from './packingListGradeSort';
import {
  packingListContainerKey,
  containerFieldsFromInput,
  forwardFillContainerInDetalle,
} from './packingListContainer';
import {
  createPackingListPdfBlobUrl,
  generatePackingListPdf,
} from './generatePackingListPDF';
import InvoicePtcPreviewModal from '../invoice/InvoicePtcPreviewModal';
import TextoAdicionalPackingListField from '../../components/TextoAdicionalPackingListField/TextoAdicionalPackingListField';
import {
  buildDefaultPackingListSummaryText,
  textoAdicionalPackingListFromCliente,
  truncateTextoAdicionalPackingList,
} from './packingListSummaryText';

/** N° de PI (texto de contrato en Excel/PDF), alineado con CrearPackingListDialog / Proformas. */
function getPiLabel(idProforma, proformas, clientes = []) {
  if (!idProforma) return '';
  const pi = proformas.find((p) => String(p.id_proforma) === String(idProforma));
  if (!pi) return '';
  return proformaEtiqueta(pi, clientes);
}

function proformaEtiqueta(pi, clientes = []) {
  if (!pi) return '';
  const idPi = pi.id_pi != null ? String(pi.id_pi).trim() : '';
  if (idPi) return idPi;
  const cliente = (clientes || []).find((c) => String(c.id_cliente) === String(pi.cliente));
  const siglas = String(cliente?.siglas || '').trim();
  const corrNum = pi.correlativo != null ? parseInt(pi.correlativo, 10) : NaN;
  const corr = Number.isFinite(corrNum) && corrNum >= 0 ? String(corrNum).padStart(2, '0') : '';
  const year = pi.fecha_embarque ? new Date(pi.fecha_embarque).getUTCFullYear() : null;
  const yy = Number.isFinite(year) ? String(year).slice(-2) : '';
  if (siglas && corr && yy) return `${siglas} ${corr}-${yy}`;
  if (corr) return siglas ? `${siglas} ${corr}` : corr;
  if (pi.id_proforma) return `PI ${pi.id_proforma}`;
  return '';
}

/** Etiqueta + id interno para buscar por correlativo o número. */
function proformaBusquedaHaystack(pi, clientes = []) {
  if (!pi) return '';
  const lbl = proformaEtiqueta(pi, clientes);
  const idn = pi.id_proforma != null ? String(pi.id_proforma) : '';
  const corr = pi.correlativo != null ? String(pi.correlativo) : '';
  return `${lbl} ${idn} ${corr}`.trim();
}

/** Último id de la lista ordenada de selección (string vacío si no hay). */
function ultimaProformaId(ids) {
  if (!ids?.length) return '';
  return String(ids[ids.length - 1]);
}

/** Mueve `id` al final si ya existía; si no, lo agrega. */
function appendProformaIdOrdenado(prevIds, id) {
  const s = String(id);
  const next = (prevIds || []).filter((x) => String(x) !== s);
  next.push(s);
  return next;
}

/** Etiquetas PI únicas ordenadas (contratos preseleccionados para columna Contract). */
function contratosPreseleccionadosLabels(proformasLista, clientes = []) {
  if (!proformasLista?.length) return [];
  const labels = proformasLista.map((p) => proformaEtiqueta(p, clientes)).filter(Boolean);
  return [...new Set(labels)].sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' })
  );
}

/** Categoría en inglés en mayúsculas (primer producto de la PI), columna Products. */
function getDefaultProductsCategoryEnFromPi(idProforma, proformas, productos, categorias) {
  if (!idProforma || !proformas?.length || !productos?.length || !categorias?.length) {
    return '';
  }
  const pi = proformas.find((p) => String(p.id_proforma) === String(idProforma));
  if (!pi || !pi.productos || !pi.productos.length) return '';
  const pid = pi.productos[0];
  const prod = productos.find((p) => String(p.id_producto) === String(pid));
  if (!prod) return '';
  const cat = categorias.find((c) => String(c.id_categoria) === String(prod.categoria));
  if (!cat) return '';
  const name = (cat.nombre_ingles && String(cat.nombre_ingles).trim()) || cat.nombre || '';
  return name ? name.toUpperCase() : '';
}

function hsFromProforma(idProforma, proformas, productos, categorias) {
  if (!idProforma || !proformas?.length || !productos?.length || !categorias?.length) {
    return '';
  }
  const pi = proformas.find((p) => String(p.id_proforma) === String(idProforma));
  if (!pi || !pi.productos || !pi.productos.length) return '';
  const pid = pi.productos[0];
  const prod = productos.find((p) => String(p.id_producto) === String(pid));
  if (!prod) return '';
  const cat = categorias.find((c) => String(c.id_categoria) === String(prod.categoria));
  return cat?.ncm != null ? String(cat.ncm).trim() : '';
}

function buildClienteDireccionSnapshot(cli) {
  if (!cli) return '';
  const parts = [];
  if (cli.direccion) parts.push(cli.direccion);
  if (cli.ciudad) parts.push(cli.ciudad);
  if (cli.codigo_postal) parts.push(`PIN CODE: ${String(cli.codigo_postal).trim()}`);
  // País no va en Address: se muestra en Destination Country.
  return parts.filter(Boolean).join('\n');
}

function poLabelsJoined(ids, proformas, clientes) {
  const parts = (ids || [])
    .map((id) => getPiLabel(id, proformas, clientes))
    .filter(Boolean);
  return parts.join('; ');
}

function groupStartContRows(rows, idx) {
  const k = packingListContainerKey(rows[idx]);
  if (!k) return idx;
  let s = idx;
  while (s > 0 && packingListContainerKey(rows[s - 1]) === k) s -= 1;
  return s;
}

function groupEndContRows(rows, idx) {
  const k = packingListContainerKey(rows[idx]);
  if (!k) return idx;
  let e = idx;
  while (e < rows.length - 1 && packingListContainerKey(rows[e + 1]) === k) e += 1;
  return e;
}

function containerMergeInfo(rows, idx) {
  const k = packingListContainerKey(rows[idx]);
  if (!k) {
    return { isFirst: true, span: 1, start: idx, end: idx };
  }
  const start = groupStartContRows(rows, idx);
  const end = groupEndContRows(rows, idx);
  return { isFirst: start === idx, span: end - start + 1, start, end };
}

function parsePlNumberInput(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const raw = String(v).trim().replace(/\s/g, '');
  if (!raw) return null;
  let normalized = raw;
  if (raw.includes(',')) {
    normalized = raw.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(raw)) {
    normalized = raw.replace(/\./g, '');
  }
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
}

function formatPlNumber(v, { min = 0, max = 2 } = {}) {
  const n = parsePlNumberInput(v);
  if (n == null || Number.isNaN(n)) return '';
  return n.toLocaleString('es-CL', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
}

function formatEspesorPl(v) {
  return formatPlNumber(v, { min: 0, max: 3 });
}

function formatMilesPl(v) {
  return formatPlNumber(v, { min: 0, max: 2 });
}

function formatM3Pl(v) {
  return formatPlNumber(v, { min: 3, max: 3 });
}

function numberToEditString(v) {
  const n = parsePlNumberInput(v);
  if (n == null) return v == null ? '' : String(v);
  const [ent, dec] = String(n).split('.');
  return dec != null ? `${ent},${dec}` : ent;
}

function FormattedNumberInput({ value, onChange, formatter = formatMilesPl, className = '', ariaLabel, ...props }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const display = focused ? draft : formatter(value);
  return (
    <Form.Control
      size="sm"
      className={`py-0 px-1 text-end ${className}`.trim()}
      value={display}
      inputMode="decimal"
      onFocus={() => {
        setDraft(numberToEditString(value));
        setFocused(true);
      }}
      onChange={(e) => {
        let s = String(e.target.value).replace(/[^\d.,-]/g, '');
        const ci = s.indexOf(',');
        if (ci >= 0) s = s.slice(0, ci + 1) + s.slice(ci + 1).replace(/[.,]/g, '');
        const di = s.indexOf('.');
        if (di >= 0 && ci < 0) s = s.slice(0, di + 1) + s.slice(di + 1).replace(/[.,]/g, '');
        setDraft(s);
        onChange(parsePlNumberInput(s));
      }}
      onBlur={() => {
        onChange(parsePlNumberInput(draft));
        setFocused(false);
      }}
      aria-label={ariaLabel}
      {...props}
    />
  );
}

function cmpClienteNombre(a, b) {
  return (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });
}

/** Texto normalizado para búsqueda: minúsculas y sin acentos (p. ej. "méxico" ≡ "mexico"). */
function foldBusquedaTexto(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Clave para unir variantes (mayúsculas, acentos, espacios). */
function paisCatalogoDedupKey(s) {
  return (s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ');
}

/** Primera letra de cada palabra (y tras guión) en mayúscula; resto en minúsculas. */
function titleCasePaisNombre(s) {
  const t = (s || '').trim().toLowerCase();
  if (!t) return '';
  return t
    .split(/\s+/)
    .filter(Boolean)
    .map((w) =>
      w
        .split('-')
        .map((seg) => (seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : ''))
        .join('-')
    )
    .join(' ');
}

// Parche exclusivo de Packing List: mostrar Brazil cuando el país sea Brasil.
function normalizeCountryForPackingList(s) {
  const t = (s || '').trim();
  if (!t) return '';
  return /^brasil$/i.test(t) ? 'Brazil' : t;
}

function buildPaisesCatalogoFromRaw(valores) {
  const byKey = new Map();
  for (const raw0 of valores) {
    const raw = String(raw0 ?? '').trim();
    if (raw.length < 3) continue;
    const key = paisCatalogoDedupKey(raw);
    if (!key) continue;
    const prev = byKey.get(key);
    if (!prev || raw.length > prev.length) byKey.set(key, raw);
  }
  const normalizedUnique = new Map();
  for (const r of Array.from(byKey.values())) {
    const normalized = normalizeCountryForPackingList(titleCasePaisNombre(r));
    if (!normalized) continue;
    const nKey = paisCatalogoDedupKey(normalized);
    if (!normalizedUnique.has(nKey)) normalizedUnique.set(nKey, normalized);
  }
  return Array.from(normalizedUnique.values())
    .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
}

function filtrarPaisesCatalogo(catalogo, texto) {
  const qf = foldBusquedaTexto(texto);
  if (!qf) return catalogo;
  return catalogo
    .filter((n) => foldBusquedaTexto(n).includes(qf))
    .slice(0, 80);
}

function puertoEtiqueta(p) {
  if (!p) return '';
  const n = (p.nombre || '').trim();
  const pa = normalizeCountryForPackingList((p.pais || '').trim());
  return pa ? `${n} (${pa})` : n;
}

function cmpPuerto(a, b) {
  const byNombre = (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });
  if (byNombre !== 0) return byNombre;
  return (a.pais || '').localeCompare(b.pais || '', 'es', { sensitivity: 'base' });
}

/**
 * Mantenedor Puertos: 1 = Origen, 2 = Destino, 3 = Origen-Destino (debe listarse en ambos).
 * {@link frontend/src/vistas/puertos/AgregarPuerto.js}
 */
function puertoIndicadorNum(p) {
  const n = Number(p?.indicador);
  return Number.isFinite(n) ? n : NaN;
}

function puertoEnListaOrigenMantenedor(p) {
  const n = puertoIndicadorNum(p);
  return n === 1 || n === 3;
}

function puertoEnListaDestinoMantenedor(p) {
  const n = puertoIndicadorNum(p);
  return n === 2 || n === 3;
}

/**
 * Formulario creación packing list en diálogo ElegantModal.
 * Validaciones: N° booking obligatorio y único; BL alfanumérico, único por N° (vs lista existente).
 */
const AgregarPackingList = ({
  show,
  cerrar,
  packingListEditId = null,
  existingBookingNumbers = [],
  existingBlPorInvoice = [],
  onGuardarExito,
}) => {
  const isEditMode = packingListEditId != null && packingListEditId !== '';
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [puertos, setPuertos] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);

  const [numeroBooking, setNumeroBooking] = useState('');
  const [fechaEmbarque, setFechaEmbarque] = useState('');
  const [idCliente, setIdCliente] = useState('');
  const [idProveedor, setIdProveedor] = useState('');
  const [paisOrigen, setPaisOrigen] = useState('');
  const [paisDestino, setPaisDestino] = useState('');
  const [nombreNave, setNombreNave] = useState('');
  const [idPuertoOrigen, setIdPuertoOrigen] = useState('');
  const [idPuertoDestino, setIdPuertoDestino] = useState('');
  const [lugarEntrega, setLugarEntrega] = useState('');
  const [nombreBl, setNombreBl] = useState('');
  /** Orden de selección: la última PI es la que alimenta Contract/Products por defecto. */
  const [idsProformaSeleccion, setIdsProformaSeleccion] = useState([]);
  const [archivoPacking, setArchivoPacking] = useState(null);
  const [totalPaquetes, setTotalPaquetes] = useState('');
  const [totalContenedores, setTotalContenedores] = useState('');
  const [detalleExcel, setDetalleExcel] = useState([]);
  const [textoAdicionalPackingList, setTextoAdicionalPackingList] = useState('');
  const [parseMeta, setParseMeta] = useState(null);
  const [archivoParseError, setArchivoParseError] = useState('');

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showGuardadoToast, setShowGuardadoToast] = useState(false);
  const [guardadoToastMensaje, setGuardadoToastMensaje] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewParamsRef = useRef(null);
  const [clienteBusqueda, setClienteBusqueda] = useState('');
  const [clienteMenuOpen, setClienteMenuOpen] = useState(false);
  const [paisOrigenMenuOpen, setPaisOrigenMenuOpen] = useState(false);
  const [paisDestinoMenuOpen, setPaisDestinoMenuOpen] = useState(false);
  const [puertoOrigenBusqueda, setPuertoOrigenBusqueda] = useState('');
  const [puertoOrigenMenuOpen, setPuertoOrigenMenuOpen] = useState(false);
  const [puertoDestinoBusqueda, setPuertoDestinoBusqueda] = useState('');
  const [puertoDestinoMenuOpen, setPuertoDestinoMenuOpen] = useState(false);

  const totalM3PorContenedor = useMemo(() => {
    const map = new Map();
    for (const row of detalleExcel || []) {
      const cont = packingListContainerKey(row) || 'Sin CTNR';
      const m3 = parsePlNumberInput(row.total_m3) || 0;
      if (!map.has(cont)) map.set(cont, 0);
      map.set(cont, map.get(cont) + m3);
    }
    return [...map.entries()]
      .map(([cont, total]) => ({ cont, total }));
  }, [detalleExcel]);

  /** Suma de M³ de todos los contenedores / líneas del packing list. */
  const totalM3Operacion = useMemo(
    () => totalM3PorContenedor.reduce((s, item) => s + (Number(item.total) || 0), 0),
    [totalM3PorContenedor]
  );

  const footerErrors = useMemo(() => {
    const msgs = [];
    if (saveError) msgs.push(saveError);
    if (archivoParseError) msgs.push(archivoParseError);
    if (previewError) msgs.push(previewError);
    Object.values(errors).forEach((m) => {
      if (m) msgs.push(m);
    });
    return msgs;
  }, [saveError, archivoParseError, previewError, errors]);
  const [proformaBusqueda, setProformaBusqueda] = useState('');
  const [proformaMenuOpen, setProformaMenuOpen] = useState(false);
  const proformaInputRef = useRef(null);
  /** Tras elegir en la lista, el blur del input no debe cerrar el menú enseguida. */
  const proformaElegidaRecienteRef = useRef(false);
  const bookingOriginalRef = useRef('');
  const blOriginalRef = useRef('');
  const packingModalLoadKeyRef = useRef(null);

  const idsProformaRef = useRef(idsProformaSeleccion);
  const proformasRef = useRef(proformas);
  const idClienteRef = useRef(idCliente);
  useEffect(() => {
    if (detalleExcel.length > 0 && errors.archivo) {
      setErrors((o) => ({ ...o, archivo: null }));
    }
  }, [detalleExcel.length, errors.archivo]);

  useEffect(() => {
    idsProformaRef.current = idsProformaSeleccion;
  }, [idsProformaSeleccion]);
  useEffect(() => {
    proformasRef.current = proformas;
  }, [proformas]);
  useEffect(() => {
    idClienteRef.current = idCliente;
  }, [idCliente]);

  useEffect(() => {
    if (show) {
      setShowGuardadoToast(false);
      setGuardadoToastMensaje('');
    }
  }, [show]);

  /** Evita pisar Contract al solo cargar catálogo proformas; sí sincroniza al cambiar PIs o Excel. */
  const contractSyncRef = useRef({ idsKey: '[]', parseMeta: null, label: '' });

  const productosRef = useRef(productos);
  const categoriasRef = useRef(categorias);
  const clientesRef = useRef(clientes);
  useEffect(() => {
    productosRef.current = productos;
  }, [productos]);
  useEffect(() => {
    categoriasRef.current = categorias;
  }, [categorias]);
  useEffect(() => {
    clientesRef.current = clientes;
  }, [clientes]);

  /** PIs cuyo campo cliente coincide con el seleccionado; sin cliente, todas. */
  const proformasFiltradas = useMemo(() => {
    if (!idCliente) return proformas;
    return proformas.filter(
      (p) => p.cliente != null && String(p.cliente) === String(idCliente)
    );
  }, [proformas, idCliente]);

  const idProformaUltima = useMemo(
    () => ultimaProformaId(idsProformaSeleccion),
    [idsProformaSeleccion]
  );

  /** Contratos (PI) disponibles para columna Contract del Excel: solo PIs seleccionadas. */
  const contratosExcelOpciones = useMemo(
    () => {
      if (!idsProformaSeleccion?.length) return [];
      const sel = new Set(idsProformaSeleccion.map((id) => String(id)));
      const proformasSel = (proformas || []).filter((p) => sel.has(String(p.id_proforma)));
      return contratosPreseleccionadosLabels(proformasSel, clientes);
    },
    [idsProformaSeleccion, proformas, clientes]
  );

  const clientesFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(clienteBusqueda);
    if (!qf) {
      return [...clientes].sort(cmpClienteNombre);
    }
    return clientes
      .filter((c) => foldBusquedaTexto(c.nombre || '').includes(qf))
      .sort(cmpClienteNombre)
      .slice(0, 80);
  }, [clientes, clienteBusqueda]);

  const paisesOrigenCatalogo = useMemo(
    () =>
      buildPaisesCatalogoFromRaw([
        ...proveedores.map((p) => p.pais),
        ...puertos.map((p) => p.pais),
      ]),
    [proveedores, puertos]
  );

  const paisesDestinoCatalogo = useMemo(
    () =>
      buildPaisesCatalogoFromRaw([
        ...clientes.map((c) => c.pais),
        ...puertos.map((p) => p.pais),
      ]),
    [clientes, puertos]
  );

  const paisesOrigenFiltrados = useMemo(
    () => filtrarPaisesCatalogo(paisesOrigenCatalogo, paisOrigen),
    [paisesOrigenCatalogo, paisOrigen]
  );

  const paisesDestinoFiltrados = useMemo(
    () => filtrarPaisesCatalogo(paisesDestinoCatalogo, paisDestino),
    [paisesDestinoCatalogo, paisDestino]
  );

  const puertosOrdenados = useMemo(
    () => [...puertos].sort(cmpPuerto),
    [puertos]
  );

  /** Según `indicador` del mantenedor de puertos (Origen, Destino u Origen-Destino en ambas listas). */
  const puertosCatalogoOrigen = useMemo(
    () => puertosOrdenados.filter((p) => puertoEnListaOrigenMantenedor(p)),
    [puertosOrdenados]
  );

  const puertosCatalogoDestino = useMemo(
    () => puertosOrdenados.filter((p) => puertoEnListaDestinoMantenedor(p)),
    [puertosOrdenados]
  );

  const puertosOrigenFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(puertoOrigenBusqueda);
    if (!qf) return puertosCatalogoOrigen;
    return puertosCatalogoOrigen
      .filter((p) => foldBusquedaTexto(puertoEtiqueta(p)).includes(qf))
      .slice(0, 80);
  }, [puertosCatalogoOrigen, puertoOrigenBusqueda]);

  const puertosDestinoFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(puertoDestinoBusqueda);
    if (!qf) return puertosCatalogoDestino;
    return puertosCatalogoDestino
      .filter((p) => foldBusquedaTexto(puertoEtiqueta(p)).includes(qf))
      .slice(0, 80);
  }, [puertosCatalogoDestino, puertoDestinoBusqueda]);

  const proformasOrdenadas = useMemo(
    () =>
      [...proformasFiltradas].sort((a, b) =>
        proformaEtiqueta(a, clientes).localeCompare(proformaEtiqueta(b, clientes), 'es', {
          sensitivity: 'base',
        })
      ),
    [proformasFiltradas, clientes]
  );

  const proformasBusquedaLista = useMemo(() => {
    const yaSeleccion = new Set((idsProformaSeleccion || []).map((id) => String(id)));
    const candidatas = proformasOrdenadas.filter(
      (pi) => !yaSeleccion.has(String(pi.id_proforma))
    );
    const qf = foldBusquedaTexto(proformaBusqueda);
    if (!qf) return candidatas.slice(0, 80);
    return candidatas
      .filter((pi) => foldBusquedaTexto(proformaBusquedaHaystack(pi, clientes)).includes(qf))
      .slice(0, 80);
  }, [proformasOrdenadas, proformaBusqueda, idsProformaSeleccion, clientes]);

  useEffect(() => {
    setIdsProformaSeleccion((prev) =>
      prev.filter((id) =>
        proformasFiltradas.some((p) => String(p.id_proforma) === String(id))
      )
    );
  }, [idCliente, proformasFiltradas]);

  const defaultProductsLabel = useMemo(
    () =>
      getDefaultProductsCategoryEnFromPi(
        idProformaUltima,
        proformas,
        productos,
        categorias
      ),
    [idProformaUltima, proformas, productos, categorias]
  );

  const buildSummaryTextContext = useCallback(
    (overrides = {}) => {
      const ptoDestino = puertos.find((p) => String(p.id_puerto) === String(idPuertoDestino));
      const pfUltima = proformas.find(
        (p) => String(p.id_proforma) === String(idProformaUltima)
      );
      return {
        productDescription: pfUltima?.descripcion_producto || '',
        hsCode: hsFromProforma(idProformaUltima, proformas, productos, categorias),
        paisOrigen,
        paisDestino,
        lugarEntrega,
        nombrePuertoDestino: ptoDestino ? puertoEtiqueta(ptoDestino) : puertoDestinoBusqueda,
        detalleExcel,
        ...overrides,
      };
    },
    [
      puertos,
      idPuertoDestino,
      proformas,
      idProformaUltima,
      productos,
      categorias,
      paisOrigen,
      paisDestino,
      lugarEntrega,
      puertoDestinoBusqueda,
      detalleExcel,
    ]
  );

  const aplicarTextoAdicionalDesdeCliente = useCallback(
    (cliente, contextOverrides = {}) => {
      const fromCliente = textoAdicionalPackingListFromCliente(cliente);
      if (fromCliente.trim()) {
        setTextoAdicionalPackingList(fromCliente);
        return;
      }
      setTextoAdicionalPackingList(
        buildDefaultPackingListSummaryText(buildSummaryTextContext(contextOverrides))
      );
    },
    [buildSummaryTextContext]
  );

  const resetForm = useCallback(() => {
    setNumeroBooking('');
    setFechaEmbarque('');
    setIdCliente('');
    setIdProveedor('');
    setPaisOrigen('');
    setPaisDestino('');
    setNombreNave('');
    setIdPuertoOrigen('');
    setIdPuertoDestino('');
    setLugarEntrega('');
    setNombreBl('');
    setIdsProformaSeleccion([]);
    setArchivoPacking(null);
    setTotalPaquetes('');
    setTotalContenedores('');
    setDetalleExcel([]);
    setTextoAdicionalPackingList('');
    setParseMeta(null);
    setArchivoParseError('');
    setErrors({});
    setSaveError('');
    setClienteBusqueda('');
    setClienteMenuOpen(false);
    setPaisOrigenMenuOpen(false);
    setPaisDestinoMenuOpen(false);
    setPuertoOrigenBusqueda('');
    setPuertoOrigenMenuOpen(false);
    setPuertoDestinoBusqueda('');
    setPuertoDestinoMenuOpen(false);
    setProformaBusqueda('');
    setProformaMenuOpen(false);
    contractSyncRef.current = { idsKey: '[]', parseMeta: null, label: '' };
    setShowPreview(false);
    setPreviewError('');
    setPreviewLoading(false);
    previewParamsRef.current = null;
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const onArchivoPackingChange = (e) => {
    const file = e.target.files?.[0] || null;
    setArchivoPacking(file);
    setArchivoParseError('');
    setDetalleExcel([]);
    setParseMeta(null);
    if (!file) {
      setTotalPaquetes('');
      setTotalContenedores('');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const buf = ev.target?.result;
      if (!buf) return;
      const res = parsePackingListExcel(buf);
      if (!res.ok) {
        setArchivoParseError(res.error || 'No se pudo interpretar el Excel.');
        return;
      }
      const rows = res.rows || [];
      const proformasAll = proformasRef.current || [];
      const idsSel = idsProformaRef.current || [];
      const idsSelSet = new Set(idsSel.map((id) => String(id)));
      const proformasParaContrato = proformasAll.filter((p) =>
        idsSelSet.has(String(p.id_proforma))
      );
      const contratosOpts = contratosPreseleccionadosLabels(
        proformasParaContrato,
        clientesRef.current || []
      );
      const idUltima = ultimaProformaId(idsSel);
      const poLabel = getPiLabel(idUltima, proformasAll, clientesRef.current || []);
      const productsDefault = getDefaultProductsCategoryEnFromPi(
        idUltima,
        proformasRef.current,
        productosRef.current,
        categoriasRef.current
      );
      setDetalleExcel(
        sortPackingListDetailByGradeWithinContainer(
          forwardFillContainerInDetalle(rows).map((row) => {
            const next = { ...row };
            if (contratosOpts.length > 0) {
              next.nf_celda = poLabel || '';
            } else if (poLabel) {
              next.nf_celda = poLabel;
            }
            if (productsDefault) next.productos_texto = productsDefault;
            return next;
          })
        )
      );
      setParseMeta(res);
      if (res.totalPaquetes != null && !Number.isNaN(res.totalPaquetes)) {
        setTotalPaquetes(String(Math.round(res.totalPaquetes * 1000) / 1000));
      }
      if (res.contenedoresEstimado != null) {
        setTotalContenedores(String(res.contenedoresEstimado));
      }
      if (rows.length > 0) {
        setErrors((o) => (o.archivo ? { ...o, archivo: null } : o));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const cargarPackingListEdicion = useCallback(async (idPl) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const res = await fetch(`${urlApiProd}/packinglists/${idPl}`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setSaveError(err.error || 'No se pudo cargar el packing list.');
      return;
    }
    const pl = await res.json();
    bookingOriginalRef.current = pl.nro_booking || '';
    blOriginalRef.current = pl.nombre_bl || '';
    setNumeroBooking(pl.nro_booking || '');
    setFechaEmbarque((pl.fecha_embarque || '').slice(0, 10));
    setIdCliente(pl.id_cliente != null ? String(pl.id_cliente) : '');
    setClienteBusqueda(pl.cliente_nombre_snapshot || '');
    setIdProveedor(pl.id_proveedor != null ? String(pl.id_proveedor) : '');
    setPaisOrigen(pl.pais_origen || '');
    setPaisDestino(pl.pais_destino || '');
    setNombreNave(pl.nombre_nave || '');
    setIdPuertoOrigen(pl.id_puerto_origen != null ? String(pl.id_puerto_origen) : '');
    setPuertoOrigenBusqueda(pl.nombre_puerto_origen || '');
    setIdPuertoDestino(pl.id_puerto_destino != null ? String(pl.id_puerto_destino) : '');
    setPuertoDestinoBusqueda(pl.nombre_puerto_destino || '');
    setLugarEntrega(pl.lugar_entrega || '');
    setNombreBl(pl.nombre_bl || '');
    setIdsProformaSeleccion((pl.id_proformas || []).map((id) => String(id)));
    setTotalPaquetes(pl.total_paquetes != null ? String(pl.total_paquetes) : '');
    setTotalContenedores(pl.total_contenedores != null ? String(pl.total_contenedores) : '');
    setDetalleExcel(
      sortPackingListDetailByGradeWithinContainer(
        forwardFillContainerInDetalle(Array.isArray(pl.lineas) ? pl.lineas : [])
      )
    );
    const snapshot = pl.texto_adicional_packing_list_snapshot;
    if (snapshot && String(snapshot).trim()) {
      setTextoAdicionalPackingList(truncateTextoAdicionalPackingList(snapshot));
    } else {
      setTextoAdicionalPackingList(
        buildDefaultPackingListSummaryText({
          productDescription: pl.product_description_snapshot || '',
          hsCode: pl.hs_code_snapshot || '',
          paisOrigen: pl.pais_origen || '',
          paisDestino: pl.pais_destino || '',
          lugarEntrega: pl.lugar_entrega || '',
          nombrePuertoDestino: pl.nombre_puerto_destino || '',
          detalleExcel: Array.isArray(pl.lineas) ? pl.lineas : [],
        })
      );
    }
    setParseMeta(pl.parse_meta || null);
    setArchivoPacking(null);
    setArchivoParseError('');
    setErrors({});
    setSaveError('');
  }, []);

  useEffect(() => {
    if (!show) {
      packingModalLoadKeyRef.current = null;
      return undefined;
    }

    const loadKey = String(packingListEditId ?? 'new');
    if (packingModalLoadKeyRef.current === loadKey) return undefined;
    packingModalLoadKeyRef.current = loadKey;

    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setSaveError('');

    (async () => {
      try {
        const [r1, r2, r3, r4, r5, r6] = await Promise.all([
          fetch(`${urlApiProd}/clientes`, { headers }),
          fetch(`${urlApiProd}/proveedores`, { headers }),
          fetch(`${urlApiProd}/puertos`, { headers }),
          fetch(`${urlApiProd}/proformas`, { headers }),
          fetch(`${urlApiProd}/productos`, { headers }),
          fetch(`${urlApiProd}/categorias`, { headers }),
        ]);
        if (cancelled) return;
        if (r1.ok) setClientes(await r1.json());
        if (r2.ok) setProveedores(await r2.json());
        if (r3.ok) setPuertos(await r3.json());
        if (r4.ok) setProformas(await r4.json());
        if (r5.ok) setProductos(await r5.json());
        if (r6.ok) setCategorias(await r6.json());
        if (packingListEditId) {
          await cargarPackingListEdicion(packingListEditId);
        } else {
          resetForm();
          bookingOriginalRef.current = '';
          blOriginalRef.current = '';
        }
      } catch (e) {
        if (!cancelled) console.error('Error cargando datos packing list:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una carga por apertura (loadKey)
  }, [show, packingListEditId]);

  /** Tras leer Excel o cambiar PI/catálogos: Products = categoría EN mayúsc. cuando aplique (última PI). */
  useEffect(() => {
    const productsDefault = getDefaultProductsCategoryEnFromPi(
      idProformaUltima,
      proformas,
      productos,
      categorias
    );
    if (!productsDefault) return;
    setDetalleExcel((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((row) => ({
        ...row,
        productos_texto: productsDefault,
      }));
    });
  }, [idProformaUltima, proformas, productos, categorias, parseMeta]);

  /** Columna Contract: sin aplicar PI del formulario al importar Excel; sí al cambiar orden/selección de PIs. */
  useEffect(() => {
    const label = getPiLabel(idProformaUltima, proformas, clientes);
    const idsKey = JSON.stringify(idsProformaSeleccion);
    const prev = contractSyncRef.current;
    const idsChanged = prev.idsKey !== idsKey;
    const metaChanged = prev.parseMeta !== parseMeta;

    if (metaChanged) {
      contractSyncRef.current = { idsKey, parseMeta, label };
      return;
    }

    contractSyncRef.current = { idsKey, parseMeta, label };

    if (!idsChanged) return;

    setDetalleExcel((prevRows) => {
      if (prevRows.length === 0) return prevRows;
      return prevRows.map((row) => {
        const next = { ...row };
        if (idProformaUltima && label) {
          next.nf_celda = label;
        } else if (!idProformaUltima) {
          next.nf_celda = '';
        }
        return next;
      });
    });
  }, [idsProformaSeleccion, idProformaUltima, proformas, clientes, parseMeta]);

  const onChangeNumeroBooking = (e) => {
    setNumeroBooking(e.target.value);
    if (errors.numeroBooking) setErrors((o) => ({ ...o, numeroBooking: null }));
  };

  const onChangeNombreBl = (e) => {
    setNombreBl(e.target.value);
    if (errors.nombreBl) setErrors((o) => ({ ...o, nombreBl: null }));
  };

  const onSelectCliente = (id) => {
    setIdCliente(id);
    const c = clientes.find((x) => String(x.id_cliente) === String(id));
    if (c) {
      const destino = normalizeCountryForPackingList(titleCasePaisNombre(c.pais || ''));
      const pid = c.puerto != null ? String(c.puerto) : '';
      const pto = pid ? puertos.find((x) => String(x.id_puerto) === pid) : null;
      const ptoLabel = pto ? puertoEtiqueta(pto) : '';
      const entrega = c.direccion_despacho || '';
      setPaisDestino(destino);
      setIdPuertoDestino(pid);
      setPuertoDestinoBusqueda(ptoLabel);
      setLugarEntrega(entrega);
      if (!isEditMode) {
        aplicarTextoAdicionalDesdeCliente(c, {
          paisDestino: destino,
          lugarEntrega: entrega,
          nombrePuertoDestino: ptoLabel,
        });
      }
    } else {
      setPaisDestino('');
      setIdPuertoDestino('');
      setPuertoDestinoBusqueda('');
      setLugarEntrega('');
      if (!isEditMode) {
        setTextoAdicionalPackingList('');
      }
    }
  };

  const onClienteBusquedaChange = (e) => {
    const v = e.target.value;
    setClienteBusqueda(v);
    setClienteMenuOpen(true);
    if (errors.idCliente) setErrors((o) => ({ ...o, idCliente: null }));
    if (idCliente) {
      const cur = clientes.find((x) => String(x.id_cliente) === String(idCliente));
      const same =
        cur && foldBusquedaTexto(cur.nombre || '') === foldBusquedaTexto(v);
      if (!same) onSelectCliente('');
    }
  };

  const elegirClienteLista = (c) => {
    onSelectCliente(String(c.id_cliente));
    setClienteBusqueda(c.nombre || '');
    setClienteMenuOpen(false);
    if (errors.idCliente) setErrors((o) => ({ ...o, idCliente: null }));
  };

  const onSelectProveedor = (id) => {
    setIdProveedor(id);
    const p = proveedores.find((x) => String(x.id_proveedor) === String(id));
    if (p) setPaisOrigen(normalizeCountryForPackingList(titleCasePaisNombre(p.pais || '')));
    else setPaisOrigen('');
  };

  const onPaisOrigenInputChange = (e) => {
    setPaisOrigen(normalizeCountryForPackingList(e.target.value));
    setPaisOrigenMenuOpen(true);
  };

  const elegirPaisOrigenLista = (nombre) => {
    setPaisOrigen(normalizeCountryForPackingList(nombre));
    setPaisOrigenMenuOpen(false);
  };

  const onPaisOrigenInputBlur = () => {
    window.setTimeout(() => {
      setPaisOrigenMenuOpen(false);
      setPaisOrigen((prev) => {
        const t = (prev || '').trim();
        if (!t) return '';
        return normalizeCountryForPackingList(titleCasePaisNombre(t));
      });
    }, 180);
  };

  const onPaisDestinoInputChange = (e) => {
    setPaisDestino(normalizeCountryForPackingList(e.target.value));
    setPaisDestinoMenuOpen(true);
  };

  const elegirPaisDestinoLista = (nombre) => {
    setPaisDestino(normalizeCountryForPackingList(nombre));
    setPaisDestinoMenuOpen(false);
  };

  const onPaisDestinoInputBlur = () => {
    window.setTimeout(() => {
      setPaisDestinoMenuOpen(false);
      setPaisDestino((prev) => {
        const t = (prev || '').trim();
        if (!t) return '';
        return normalizeCountryForPackingList(titleCasePaisNombre(t));
      });
    }, 180);
  };

  const onPuertoOrigenBusquedaChange = (e) => {
    const v = e.target.value;
    setPuertoOrigenBusqueda(v);
    setPuertoOrigenMenuOpen(true);
    if (idPuertoOrigen) {
      const p = puertos.find((x) => String(x.id_puerto) === String(idPuertoOrigen));
      const same =
        p && foldBusquedaTexto(puertoEtiqueta(p)) === foldBusquedaTexto(v);
      if (!same) setIdPuertoOrigen('');
    }
  };

  const elegirPuertoOrigenLista = (p) => {
    setIdPuertoOrigen(String(p.id_puerto));
    setPuertoOrigenBusqueda(puertoEtiqueta(p));
    setPuertoOrigenMenuOpen(false);
  };

  const onPuertoOrigenInputBlur = () => {
    window.setTimeout(() => setPuertoOrigenMenuOpen(false), 180);
  };

  const onPuertoDestinoBusquedaChange = (e) => {
    const v = e.target.value;
    setPuertoDestinoBusqueda(v);
    setPuertoDestinoMenuOpen(true);
    if (idPuertoDestino) {
      const p = puertos.find((x) => String(x.id_puerto) === String(idPuertoDestino));
      const same =
        p && foldBusquedaTexto(puertoEtiqueta(p)) === foldBusquedaTexto(v);
      if (!same) setIdPuertoDestino('');
    }
  };

  const elegirPuertoDestinoLista = (p) => {
    setIdPuertoDestino(String(p.id_puerto));
    setPuertoDestinoBusqueda(puertoEtiqueta(p));
    setPuertoDestinoMenuOpen(false);
  };

  const onPuertoDestinoInputBlur = () => {
    window.setTimeout(() => setPuertoDestinoMenuOpen(false), 180);
  };

  const onProformaBusquedaChange = (e) => {
    const v = e.target.value;
    setProformaBusqueda(v);
    setProformaMenuOpen(true);
  };

  const elegirProformaLista = (pi) => {
    proformaElegidaRecienteRef.current = true;
    setIdsProformaSeleccion((prev) => appendProformaIdOrdenado(prev, pi.id_proforma));
    setProformaBusqueda('');
    setProformaMenuOpen(true);
    if (errors.idProformas) setErrors((o) => ({ ...o, idProformas: null }));
    window.setTimeout(() => {
      proformaInputRef.current?.focus();
    }, 0);
    window.setTimeout(() => {
      proformaElegidaRecienteRef.current = false;
    }, 400);
  };

  const quitarProformaChip = (idProforma) => {
    setIdsProformaSeleccion((prev) => prev.filter((x) => String(x) !== String(idProforma)));
  };

  const onProformaInputBlur = () => {
    window.setTimeout(() => {
      if (proformaElegidaRecienteRef.current) {
        setProformaMenuOpen(true);
        return;
      }
      setProformaMenuOpen(false);
    }, 180);
  };

  const patchDetalleRow = useCallback((idx, patch) => {
    setDetalleExcel((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }, []);

  /** Mismo valor en todas las filas del mismo contenedor consecutivo (como en el Excel). */
  const patchDetalleByContainerGroup = useCallback((idx, patch) => {
    setDetalleExcel((prev) => {
      if (!prev[idx]) return prev;
      const next = [...prev];
      const k = packingListContainerKey(prev[idx]);
      if (!k) {
        next[idx] = { ...next[idx], ...patch };
        return next;
      }
      const s = groupStartContRows(prev, idx);
      const e = groupEndContRows(prev, idx);
      for (let i = s; i <= e; i += 1) {
        next[i] = { ...next[i], ...patch };
      }
      return next;
    });
  }, []);

  const validate = () => {
    const e = {};
    if (!numeroBooking.trim()) {
      e.numeroBooking = 'Ingrese el N° Booking.';
    } else {
      const bk = numeroBooking.trim();
      const bookingDuplicado =
        existingBookingNumbers.includes(bk) &&
        !(isEditMode && bk === bookingOriginalRef.current);
      if (bookingDuplicado) e.numeroBooking = 'Este N° Booking ya existe.';
    }
    if (!fechaEmbarque) e.fechaEmbarque = 'Indique fecha de embarque.';
    if (!idCliente) e.idCliente = 'Busque y elija un cliente de la lista.';
    if (!idsProformaSeleccion?.length) {
      e.idProformas = 'Seleccione al menos una PI de la lista.';
    }
    if (!nombreBl.trim()) e.nombreBl = 'Ingrese el nombre del BL.';
    const blNorm = nombreBl.trim();
    if (blNorm && numeroBooking.trim()) {
      const bk = numeroBooking.trim();
      const dup = existingBlPorInvoice.some(
        (row) =>
          String(row.nro_booking || '') === bk &&
          String(row.nombre_bl).toLowerCase() === blNorm.toLowerCase()
      );
      const blSinCambioEnEdicion =
        isEditMode &&
        bk === bookingOriginalRef.current &&
        blNorm.toLowerCase() === String(blOriginalRef.current).trim().toLowerCase();
      if (dup && !blSinCambioEnEdicion) {
        e.nombreBl = 'Este BL ya está registrado para este N°.';
      }
    }
    if (!detalleExcel.length) {
      e.archivo = 'Cargue el Excel del packing list con líneas de detalle.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validatePreview = () => {
    const e = { ...errors };
    if (!fechaEmbarque) e.fechaEmbarque = 'Indique fecha de embarque.';
    else delete e.fechaEmbarque;
    if (!idCliente) e.idCliente = 'Busque y elija un cliente de la lista.';
    else delete e.idCliente;
    if (!detalleExcel.length) {
      e.archivo = 'Cargue el Excel del packing list con líneas de detalle.';
    } else {
      delete e.archivo;
    }
    setErrors(e);
    return Boolean(fechaEmbarque && idCliente && detalleExcel.length > 0);
  };

  const buildPdfParamsFromForm = useCallback(() => {
    const cli = clientes.find((c) => String(c.id_cliente) === String(idCliente));
    const ptoOrigen = puertos.find((p) => String(p.id_puerto) === String(idPuertoOrigen));
    const ptoDestino = puertos.find((p) => String(p.id_puerto) === String(idPuertoDestino));
    const pfUltima = proformas.find(
      (p) => String(p.id_proforma) === String(idProformaUltima)
    );
    const totalVm3 =
      detalleExcel.length > 0
        ? totalM3Operacion
        : parseMeta?.totalVolumenM3 != null && !Number.isNaN(Number(parseMeta.totalVolumenM3))
          ? Number(parseMeta.totalVolumenM3)
          : null;
    return {
      numeroInvoice: '',
      fechaEmbarque,
      clienteNombre: cli?.nombre || clienteBusqueda || '',
      clienteDireccion: buildClienteDireccionSnapshot(cli),
      clienteCodigoPostal: cli?.codigo_postal || '',
      poLabel: poLabelsJoined(idsProformaSeleccion, proformas, clientes),
      paisOrigen,
      paisDestino,
      nombreNave,
      nombrePuertoOrigen: ptoOrigen ? puertoEtiqueta(ptoOrigen) : puertoOrigenBusqueda,
      nombrePuertoDestino: ptoDestino ? puertoEtiqueta(ptoDestino) : puertoDestinoBusqueda,
      lugarEntrega,
      nombreBl: nombreBl.trim(),
      totalPaquetes: totalPaquetes === '' ? null : Number.parseFloat(totalPaquetes),
      totalContenedores:
        totalContenedores === '' ? null : Number.parseFloat(totalContenedores),
      detalleExcel,
      totalVolumenM3: totalVm3,
      productDescription: pfUltima?.descripcion_producto || '',
      hsCode: hsFromProforma(idProformaUltima, proformas, productos, categorias),
      summaryAdditionalText: textoAdicionalPackingList,
    };
  }, [
    clientes,
    idCliente,
    clienteBusqueda,
    puertos,
    idPuertoOrigen,
    idPuertoDestino,
    puertoOrigenBusqueda,
    puertoDestinoBusqueda,
    proformas,
    idProformaUltima,
    idsProformaSeleccion,
    parseMeta,
    fechaEmbarque,
    paisOrigen,
    paisDestino,
    nombreNave,
    lugarEntrega,
    nombreBl,
    totalPaquetes,
    totalContenedores,
    detalleExcel,
    totalM3Operacion,
    productos,
    categorias,
    textoAdicionalPackingList,
  ]);

  const cerrarPreview = useCallback(() => {
    setShowPreview(false);
    setPreviewError('');
    previewParamsRef.current = null;
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleVistaPrevia = () => {
    setPreviewError('');
    if (!validatePreview()) return;
    setPreviewLoading(true);
    setShowPreview(true);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const params = buildPdfParamsFromForm();
      previewParamsRef.current = params;
      setPreviewUrl(createPackingListPdfBlobUrl(params));
    } catch (err) {
      setPreviewError(err.message || 'No se pudo generar la vista previa.');
      previewParamsRef.current = null;
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadPreview = () => {
    const params = previewParamsRef.current;
    if (!params) return;
    generatePackingListPdf(params);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaveError('');
    setSaving(true);
    const payload = {
      nro_booking: numeroBooking.trim(),
      fecha_embarque: fechaEmbarque,
      id_cliente: idCliente ? parseInt(idCliente, 10) : null,
      id_proveedor: idProveedor ? parseInt(idProveedor, 10) : null,
      pais_origen: normalizeCountryForPackingList(paisOrigen),
      pais_destino: normalizeCountryForPackingList(paisDestino),
      nombre_nave: nombreNave,
      id_puerto_origen: idPuertoOrigen ? parseInt(idPuertoOrigen, 10) : null,
      id_puerto_destino: idPuertoDestino ? parseInt(idPuertoDestino, 10) : null,
      lugar_entrega: lugarEntrega,
      nombre_bl: nombreBl.trim(),
      id_proformas: idsProformaSeleccion.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n)),
      total_paquetes: totalPaquetes === '' ? null : Number.parseFloat(totalPaquetes),
      total_contenedores: totalContenedores === '' ? null : Number.parseFloat(totalContenedores),
      total_volumen_m3: detalleExcel.length ? totalM3Operacion : null,
      detalle_packing: forwardFillContainerInDetalle(detalleExcel),
      parse_meta: {
        ...(parseMeta || {}),
        totalVolumenM3: detalleExcel.length ? totalM3Operacion : parseMeta?.totalVolumenM3 ?? null,
      },
      texto_adicional_packing_list_snapshot: truncateTextoAdicionalPackingList(textoAdicionalPackingList),
    };
    try {
      const token = localStorage.getItem('token');
      const url = isEditMode
        ? `${urlApiProd}/packinglists/${packingListEditId}`
        : `${urlApiProd}/packinglists`;
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(body.error || `Error ${res.status}`);
        return;
      }
      setGuardadoToastMensaje(
        isEditMode ? 'Packing list editado correctamente' : 'Packing list creado exitosamente'
      );
      setShowGuardadoToast(true);
      if (typeof onGuardarExito === 'function') onGuardarExito();
      cerrar();
    } catch (e) {
      setSaveError(e.message || 'Error de red');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SistemaToast
        show={showGuardadoToast}
        message={guardadoToastMensaje}
        onClose={() => setShowGuardadoToast(false)}
        delay={3500}
        zIndex={99999}
      />
      <ElegantModal
      show={show}
      onClose={cerrar}
      dismissable={false}
      title={isEditMode ? 'Editar packing list' : 'Crear packing list'}
      subtitle={
        isEditMode
          ? 'Modifique los datos del envío y el detalle del packing list.'
          : 'Complete los datos del envío y cargue el archivo Excel del packing list.'
      }
      titleId="packinglist-title"
      size="xl"
      footer={
        loading ? null : (
          <div className="d-flex align-items-center gap-3 flex-wrap w-100">
            <div
              className="flex-grow-1 text-danger small text-start"
              style={{ minWidth: 0 }}
              role={footerErrors.length ? 'alert' : undefined}
              aria-live="polite"
            >
              {footerErrors.length ? (
                <ul className="mb-0 ps-3">
                  {footerErrors.map((msg, i) => (
                    <li key={`${i}-${msg}`}>{msg}</li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="d-flex gap-2 flex-shrink-0 ms-auto">
            <Button variant="secondary" type="button" onClick={cerrar} disabled={saving || previewLoading}>
              Cancelar
            </Button>
            <Button
              variant="outline-primary"
              type="button"
              onClick={handleVistaPrevia}
              disabled={saving || previewLoading}
            >
              {previewLoading ? 'Generando…' : 'Vista previa'}
            </Button>
            <Button variant="primary" type="submit" form="packinglist-form-add" disabled={saving || previewLoading}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
            </div>
          </div>
        )
      }
    >
      <form id="packinglist-form-add" onSubmit={handleSubmit}>
        {loading ? (
          <p className="text-center text-muted mb-0">Cargando datos…</p>
        ) : (
          <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>N° Booking</Form.Label>
                        <Form.Control
                          type="text"
                          value={numeroBooking}
                          onChange={onChangeNumeroBooking}
                          isInvalid={!!errors.numeroBooking}
                          placeholder="Ej: BK-2024-01"
                          autoComplete="off"
                        />
                        <Form.Control.Feedback type="invalid">{errors.numeroBooking}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Fecha embarque</Form.Label>
                        <DateInput
                          value={fechaEmbarque}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFechaEmbarque(v);
                            if (v && errors.fechaEmbarque) {
                              setErrors((o) => ({ ...o, fechaEmbarque: null }));
                            }
                          }}
                          isInvalid={!!errors.fechaEmbarque}
                        />
                        <Form.Control.Feedback type="invalid">{errors.fechaEmbarque}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>Cliente / Customer</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          placeholder="Escriba para buscar cliente…"
                          value={clienteBusqueda}
                          onChange={onClienteBusquedaChange}
                          onFocus={() => setClienteMenuOpen(true)}
                          onBlur={() => {
                            window.setTimeout(() => setClienteMenuOpen(false), 180);
                          }}
                          isInvalid={!!errors.idCliente}
                        />
                        {clienteMenuOpen && clientesFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 2,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {clientesFiltrados.map((c) => (
                              <ListGroup.Item
                                key={c.id_cliente}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirClienteLista(c);
                                }}
                              >
                                {c.nombre}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                        <Form.Control.Feedback type="invalid">{errors.idCliente}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Proveedor (país origen)</Form.Label>
                        <Form.Select value={idProveedor} onChange={(e) => onSelectProveedor(e.target.value)}>
                          <option value="">Seleccione…</option>
                          {proveedores.map((p) => (
                            <option key={p.id_proveedor} value={p.id_proveedor}>
                              {p.nombre}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">País origen / Country of origin (desde proveedor, editable abajo)</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>País origen / Country of origin</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={paisOrigen}
                          onChange={onPaisOrigenInputChange}
                          onFocus={() => setPaisOrigenMenuOpen(true)}
                          onBlur={onPaisOrigenInputBlur}
                          placeholder="Escriba para filtrar o elija de la lista…"
                        />
                        {paisOrigenMenuOpen && paisesOrigenFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 2,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {paisesOrigenFiltrados.map((nombre) => (
                              <ListGroup.Item
                                key={nombre}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPaisOrigenLista(nombre);
                                }}
                              >
                                {nombre}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>País destino / Destination country</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={paisDestino}
                          onChange={onPaisDestinoInputChange}
                          onFocus={() => setPaisDestinoMenuOpen(true)}
                          onBlur={onPaisDestinoInputBlur}
                          placeholder="Escriba para filtrar o elija de la lista…"
                        />
                        {paisDestinoMenuOpen && paisesDestinoFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 2,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {paisesDestinoFiltrados.map((nombre) => (
                              <ListGroup.Item
                                key={nombre}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPaisDestinoLista(nombre);
                                }}
                              >
                                {nombre}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Nombre nave / Vessel name</Form.Label>
                        <Form.Control value={nombreNave} onChange={(e) => setNombreNave(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>Puerto origen / Loading port</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={puertoOrigenBusqueda}
                          onChange={onPuertoOrigenBusquedaChange}
                          onFocus={() => setPuertoOrigenMenuOpen(true)}
                          onBlur={onPuertoOrigenInputBlur}
                          placeholder="Escriba para buscar puerto…"
                        />
                        {puertoOrigenMenuOpen && puertosOrigenFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 3,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {puertosOrigenFiltrados.map((p) => (
                              <ListGroup.Item
                                key={p.id_puerto}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPuertoOrigenLista(p);
                                }}
                              >
                                {puertoEtiqueta(p)}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                        <Form.Text className="text-muted">
                          Lista según mantenedor Puertos: tipo Origen u Origen-Destino.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>Puerto destino / Unloading port</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={puertoDestinoBusqueda}
                          onChange={onPuertoDestinoBusquedaChange}
                          onFocus={() => setPuertoDestinoMenuOpen(true)}
                          onBlur={onPuertoDestinoInputBlur}
                          placeholder="Escriba para buscar puerto…"
                        />
                        {puertoDestinoMenuOpen && puertosDestinoFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 4,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {puertosDestinoFiltrados.map((p) => (
                              <ListGroup.Item
                                key={`d-${p.id_puerto}`}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPuertoDestinoLista(p);
                                }}
                              >
                                {puertoEtiqueta(p)}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                        <Form.Text className="text-muted">
                          Lista según mantenedor Puertos: tipo Destino u Origen-Destino. Sugerido al elegir cliente;
                          puede cambiar.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Lugar de entrega / Place of delivery</Form.Label>
                        <Form.Control
                          value={lugarEntrega}
                          onChange={(e) => setLugarEntrega(e.target.value)}
                          placeholder="Desde dirección despacho del cliente"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <TextoAdicionalPackingListField
                        value={textoAdicionalPackingList}
                        onChange={(value) =>
                          setTextoAdicionalPackingList(truncateTextoAdicionalPackingList(value))
                        }
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Nombre BL (único por N°)</Form.Label>
                        <Form.Control
                          value={nombreBl}
                          onChange={onChangeNombreBl}
                          isInvalid={!!errors.nombreBl}
                        />
                        <Form.Control.Feedback type="invalid">{errors.nombreBl}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>PIs (varias; la última define Contract/Products por defecto)</Form.Label>
                        {idsProformaSeleccion.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {idsProformaSeleccion.map((idStr) => {
                              const pi = proformasFiltradas.find(
                                (p) => String(p.id_proforma) === String(idStr)
                              );
                              const lbl =
                                pi != null ? proformaEtiqueta(pi, clientes) : `id ${idStr}`;
                              return (
                                <Badge
                                  key={String(idStr)}
                                  bg="secondary"
                                  className="d-inline-flex align-items-center gap-1 fw-normal py-1 px-2"
                                >
                                  <span>{lbl}</span>
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="text-white text-decoration-none p-0 lh-1"
                                    style={{ fontSize: '1rem', minWidth: '1rem' }}
                                    onClick={() => quitarProformaChip(idStr)}
                                    aria-label={`Quitar ${lbl}`}
                                  >
                                    ×
                                  </Button>
                                </Badge>
                              );
                            })}
                          </div>
                        ) : null}
                        <div className="position-relative">
                          <Form.Control
                            ref={proformaInputRef}
                            type="text"
                            autoComplete="off"
                            value={proformaBusqueda}
                            onChange={onProformaBusquedaChange}
                            onFocus={() => setProformaMenuOpen(true)}
                            onBlur={onProformaInputBlur}
                            placeholder="Buscar y agregar PI (número o correlativo)…"
                            isInvalid={!!errors.idProformas}
                          />
                          <Form.Control.Feedback type="invalid">{errors.idProformas}</Form.Control.Feedback>
                          {proformaMenuOpen && proformasBusquedaLista.length > 0 ? (
                            <ListGroup
                              className="position-absolute w-100 shadow border rounded overflow-auto"
                              style={{
                                zIndex: 5,
                                top: '100%',
                                marginTop: 2,
                                maxHeight: 220,
                              }}
                            >
                              {proformasBusquedaLista.map((pi) => (
                                <ListGroup.Item
                                  key={pi.id_proforma}
                                  action
                                  className="py-2 px-3"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    elegirProformaLista(pi);
                                  }}
                                >
                                  {proformaEtiqueta(pi, clientes)}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          ) : null}
                        </div>
                        <Form.Text className="text-muted">
                          {idCliente
                            ? 'Solo PIs del cliente seleccionado. Re-elegir una PI la pasa al final (queda como última).'
                            : 'Elija un cliente para acotar la lista; sin cliente se muestran todas las PIs.'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Carga de packing list (archivo)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={onArchivoPackingChange}
                        />
                        <Form.Text className="text-muted">
                          Se acepta GRADO, TRATAMIENTO o TRATAMENTO en la cuarta columna. Se detecta la tabla por
                          nombres de columnas (Paquetes, Pcs/paq, Total piezas,
                          Espesor, Ancho, Largo, Volumen/M3, Peso). Si hay dos tablas iguales, se usa la
                          inferior. Los totales de paquetes y contenedores se calculan al cargar el archivo.
                        </Form.Text>
                        {parseMeta && detalleExcel.length > 0 && (
                          <Alert variant="success" className="mt-2 mb-0 py-2 small">
                            Tabla detectada en fila {parseMeta.headerRowIndex + 1}. {detalleExcel.length}{' '}
                            fila(s). Columnas:{' '}
                            {Object.entries(parseMeta.columnLetters || {})
                              .map(([k, letra]) => `${k}→${letra}`)
                              .join(', ')}
                          </Alert>
                        )}
                        {detalleExcel.length > 0 && (
                          <div className="mt-2 table-responsive" style={{ maxHeight: 400 }}>
                            <Table size="sm" bordered striped hover className="mb-0 small align-middle pl-excel-table">
                              <thead>
                                <tr>
                                  <th className="text-nowrap">#</th>
                                  <th>Contract</th>
                                  <th>Products</th>
                                  <th>Paquetes</th>
                                  <th>Pcs/paq</th>
                                  <th>Total pcs</th>
                                  <th>Esp.</th>
                                  <th>Ancho</th>
                                  <th>Largo</th>
                                  <th>M³</th>
                                  <th>Gross kg</th>
                                  <th>Net weight</th>
                                  <th>Container</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detalleExcel.map((row, idx) => {
                                  const cM = containerMergeInfo(detalleExcel, idx);
                                  const k = packingListContainerKey(row);
                                  const skipPesoBlock = k && !cM.isFirst;
                                  const s = cM.start;
                                  const fromStart = detalleExcel[s];
                                  const prevK =
                                    idx > 0 ? packingListContainerKey(detalleExcel[idx - 1]) : k;
                                  const containerBreak = idx > 0 && k && k !== prevK;
                                  return (
                                    <tr
                                      key={`${row._filaExcel}-${idx}`}
                                      className={containerBreak ? 'pl-excel-container-break' : undefined}
                                    >
                                      <td className="text-muted">{row._filaExcel}</td>
                                      <td style={{ minWidth: 100 }}>
                                        {contratosExcelOpciones.length > 0 ? (
                                          <Form.Select
                                            size="sm"
                                            className="py-0 px-1"
                                            value={row.nf_celda ?? ''}
                                            onChange={(e) =>
                                              patchDetalleRow(idx, { nf_celda: e.target.value })
                                            }
                                            aria-label={`Contract fila ${row._filaExcel}`}
                                          >
                                            <option value="">Seleccionar PI</option>
                                            {row.nf_celda != null &&
                                            String(row.nf_celda).trim() !== '' &&
                                            !contratosExcelOpciones.includes(String(row.nf_celda).trim()) ? (
                                              <option value={String(row.nf_celda).trim()}>
                                                {String(row.nf_celda).trim()} (Excel)
                                              </option>
                                            ) : null}
                                            {contratosExcelOpciones.map((opt) => (
                                              <option key={opt} value={opt}>
                                                {opt}
                                              </option>
                                            ))}
                                          </Form.Select>
                                        ) : (
                                          <Form.Control
                                            size="sm"
                                            className="py-0 px-1"
                                            value={row.nf_celda ?? ''}
                                            onChange={(e) =>
                                              patchDetalleRow(idx, { nf_celda: e.target.value })
                                            }
                                            aria-label={`Contract fila ${row._filaExcel}`}
                                          />
                                        )}
                                      </td>
                                      <td style={{ minWidth: 120 }}>
                                        <Form.Control
                                          size="sm"
                                          className="py-0 px-1"
                                          value={
                                            row.productos_texto ??
                                            defaultProductsLabel ??
                                            [
                                              row.grado_celda,
                                              row.proveedor_celda,
                                              row.almacen_celda,
                                            ]
                                              .filter(Boolean)
                                              .join(' / ')
                                          }
                                          onChange={(e) => patchDetalleRow(idx, { productos_texto: e.target.value })}
                                          aria-label={`Products fila ${row._filaExcel}`}
                                        />
                                      </td>
                                      <td className="text-end">{formatMilesPl(row.cantidad_paquetes)}</td>
                                      <td className="text-end p-0" style={{ minWidth: 72 }}>
                                        {isRwNrPackingListRow(row) ? (
                                          <FormattedNumberInput
                                            value={
                                              row.piezas_por_paquete == null ? '' : row.piezas_por_paquete
                                            }
                                            onChange={(v) =>
                                              patchDetalleRow(idx, { piezas_por_paquete: v })
                                            }
                                            formatter={(v) => formatPlNumber(v, { min: 0, max: 0 })}
                                            ariaLabel={`Pcs/paq fila ${row._filaExcel}`}
                                          />
                                        ) : (
                                          formatMilesPl(row.piezas_por_paquete)
                                        )}
                                      </td>
                                      <td className="text-end">{isRwNrPackingListRow(row) ? '' : formatMilesPl(row.total_piezas)}</td>
                                      <td className="text-end">{formatEspesorPl(row.espesor)}</td>
                                      <td className="text-end">{formatMilesPl(row.ancho)}</td>
                                      <td className="text-end">{formatMilesPl(row.largo)}</td>
                                      <td className="text-end">{formatM3Pl(row.total_m3)}</td>
                                      {!skipPesoBlock ? (
                                        <>
                                          <td className="align-middle p-0" style={{ minWidth: 72 }} rowSpan={cM.span}>
                                            <FormattedNumberInput
                                              value={fromStart.peso == null ? '' : fromStart.peso}
                                              onChange={(v) => patchDetalleByContainerGroup(s, { peso: v })}
                                              formatter={formatMilesPl}
                                              title={
                                                k && cM.span > 1
                                                  ? `Mismo contenedor: ${cM.span} filas`
                                                  : undefined
                                              }
                                              ariaLabel={`Gross kg (bloque contenedor, fila ${row._filaExcel})`}
                                            />
                                          </td>
                                          <td className="align-middle p-0" style={{ minWidth: 72 }} rowSpan={cM.span}>
                                            <FormattedNumberInput
                                              value={
                                                fromStart.peso_neto == null ? '' : fromStart.peso_neto
                                              }
                                              onChange={(v) => patchDetalleByContainerGroup(s, { peso_neto: v })}
                                              formatter={formatMilesPl}
                                              ariaLabel={`Net weight (bloque contenedor, fila ${row._filaExcel})`}
                                            />
                                          </td>
                                          <td className="align-middle p-0" style={{ minWidth: 88 }} rowSpan={cM.span}>
                                            <Form.Control
                                              size="sm"
                                              className="py-0 px-1 text-end"
                                              value={packingListContainerKey(fromStart)}
                                              onChange={(e) =>
                                                patchDetalleByContainerGroup(
                                                  s,
                                                  containerFieldsFromInput(e.target.value)
                                                )
                                              }
                                              aria-label={`Container (bloque, fila ${row._filaExcel})`}
                                            />
                                          </td>
                                        </>
                                      ) : null}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Total paquetes / Total packages</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          readOnly
                          value={totalPaquetes}
                          placeholder="0 o desde Excel"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Total contenedores / Container</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          readOnly
                          value={totalContenedores}
                          placeholder="0 o desde Excel"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Total M³</Form.Label>
                        <Form.Control
                          readOnly
                          className="text-end"
                          value={detalleExcel.length ? formatM3Pl(totalM3Operacion) : ''}
                          placeholder="0,000"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    {totalM3PorContenedor.length ? (
                      totalM3PorContenedor.map(({ cont, total }) => (
                        <Col md={4} key={cont}>
                          <Form.Group>
                            <Form.Label>Total M³ CTNR {cont}</Form.Label>
                            <Form.Control readOnly className="text-end" value={formatM3Pl(total)} />
                          </Form.Group>
                        </Col>
                      ))
                    ) : (
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Total M³ CTNR</Form.Label>
                          <Form.Control readOnly className="text-end" value="" placeholder="0,000" />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>

          </>
        )}
      </form>
    </ElegantModal>
    <InvoicePtcPreviewModal
      show={show && showPreview}
      pdfUrl={previewUrl}
      loading={previewLoading}
      error={previewError}
      onClose={cerrarPreview}
      onDownload={handleDownloadPreview}
      title="Vista previa — Packing list"
      iframeTitle="Vista previa Packing list"
    />
    </>
  );
};

export default AgregarPackingList;
